/**
 * OverageRatesCard Component
 *
 * Displays additional charges that may apply if the customer
 * exceeds included tonnage or rental period. Helps set expectations
 * for potential extra costs in a neutral, informative way.
 */

import { Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../utils/formatCurrency';

export interface OverageRatesCardProps {
  overagePerTon: number;
  dailyRentalRate: number;
  includedTonnage: number;
  rentalDays: number;
}

export function OverageRatesCard({
  overagePerTon,
  dailyRentalRate,
  includedTonnage,
  rentalDays,
}: OverageRatesCardProps) {
  return (
    <Card className="border-blue-200 bg-blue-50/30">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Info className="h-5 w-5 text-blue-600" aria-hidden="true" />
          Additional Charges (if applicable)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex justify-between items-baseline">
            <span className="font-medium text-gray-900">Weight Overage:</span>
            <span className="text-gray-900">{formatCurrency(overagePerTon)}/ton</span>
          </div>
          <p className="text-xs text-gray-600 mt-1">
            • Applies to weight over {includedTonnage} tons
          </p>
        </div>

        <div>
          <div className="flex justify-between items-baseline">
            <span className="font-medium text-gray-900">Extended Rental:</span>
            <span className="text-gray-900">{formatCurrency(dailyRentalRate)}/day</span>
          </div>
          <p className="text-xs text-gray-600 mt-1">
            • After your {rentalDays}-day rental period
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
